import { Text } from '@chakra-ui/react';
import type { InvoicingCompositionResponse } from '../../../types/reporting';
import { PRIMARY_CHART_COLOR } from '../shared/formatters';
import { ReportingDonut, type ReportingDonutSlice } from '../shared/ReportingDonut';

const FLAGGED_COLORS = {
    theirFault: '#805AD5',
    neededFlags: PRIMARY_CHART_COLOR,
    match: '#63B3ED',
} as const;

const STRIPE_COLORS = {
    postedOpen: PRIMARY_CHART_COLOR,
    postedArchived: '#63B3ED',
    flaggedOpen: '#805AD5',
    flaggedArchived: '#B794F4',
} as const;

/** Slice rows for the chart download. Labels match the doughnut. */
export function invoicingCompositionCsvRows(
    metric: string,
    data: InvoicingCompositionResponse | undefined,
): Array<{ slice: string; count: number }> {
    if (!data) return [];
    if (metric === 'flaggedFaultBreakdown') {
        const { flagged } = data;
        return [
            { slice: 'Shop-side flags', count: flagged.theirFault },
            { slice: 'Needed flags', count: flagged.neededFlags },
            { slice: 'Yaxle-side flags', count: flagged.match },
            { slice: 'Total', count: flagged.total },
        ];
    }
    if (metric === 'stripeBillingMix') {
        const { stripe } = data;
        return [
            { slice: 'Submitted', count: stripe.postedOpen },
            { slice: 'Submitted, archived', count: stripe.postedArchived },
            { slice: 'Flagged', count: stripe.flaggedOpen },
            { slice: 'Flagged, archived', count: stripe.flaggedArchived },
            { slice: 'Total', count: stripe.total },
        ];
    }
    return [];
}

function slicesWithCounts(
    items: Array<{ name: string; value: number; color: string; seriesKey: string }>,
    clickable: boolean,
): ReportingDonutSlice[] {
    return items
        .filter((item) => item.value > 0)
        .map((item) => ({ ...item, rawValue: item.value, clickable }));
}

export function InvoicingCompositionDonut({
    metric,
    data,
    isFullscreen = false,
    onSliceClick,
}: {
    metric: 'flaggedFaultBreakdown' | 'stripeBillingMix';
    data?: InvoicingCompositionResponse;
    isFullscreen?: boolean;
    onSliceClick?: (seriesKey: string, seriesLabel: string) => void;
}) {
    if (!data) {
        return <Text color="gray.400" fontSize="sm">No data</Text>;
    }

    if (metric === 'flaggedFaultBreakdown') {
        const { flagged } = data;
        if (flagged.total === 0) {
            return <Text color="gray.500" fontSize="sm">No flagged submitted invoices in this period.</Text>;
        }
        const slices = slicesWithCounts([
            { name: 'Shop-side flags', value: flagged.theirFault, color: FLAGGED_COLORS.theirFault, seriesKey: 'efficiency:theirFault' },
            { name: 'Needed flags', value: flagged.neededFlags, color: FLAGGED_COLORS.neededFlags, seriesKey: 'efficiency:neededFlags' },
            { name: 'Yaxle-side flags', value: flagged.match, color: FLAGGED_COLORS.match, seriesKey: 'efficiency:match' },
        ], Boolean(onSliceClick));
        return (
            <ReportingDonut
                slices={slices}
                isFullscreen={isFullscreen}
                valueFormat="number"
                labelAsShare
                metricLabel="Flagged invoices"
                onSliceClick={onSliceClick
                    ? (slice) => {
                        if (slice.seriesKey) onSliceClick(slice.seriesKey, slice.name);
                    }
                    : undefined}
            />
        );
    }

    const { stripe } = data;
    if (stripe.total === 0) {
        return <Text color="gray.500" fontSize="sm">No billable invoices in this period.</Text>;
    }
    const slices = slicesWithCounts([
        { name: 'Submitted', value: stripe.postedOpen, color: STRIPE_COLORS.postedOpen, seriesKey: 'billable:postedOpen' },
        { name: 'Submitted, archived', value: stripe.postedArchived, color: STRIPE_COLORS.postedArchived, seriesKey: 'billable:postedArchived' },
        { name: 'Flagged', value: stripe.flaggedOpen, color: STRIPE_COLORS.flaggedOpen, seriesKey: 'billable:flaggedOpen' },
        { name: 'Flagged, archived', value: stripe.flaggedArchived, color: STRIPE_COLORS.flaggedArchived, seriesKey: 'billable:flaggedArchived' },
    ], Boolean(onSliceClick));
    return (
        <ReportingDonut
            slices={slices}
            isFullscreen={isFullscreen}
            valueFormat="number"
            labelAsShare
            metricLabel="Billable invoices"
            onSliceClick={onSliceClick
                ? (slice) => {
                    if (slice.seriesKey) onSliceClick(slice.seriesKey, slice.name);
                }
                : undefined}
        />
    );
}
